import React, { useState, useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Package, Briefcase, Users, FileText, LogOut, FolderTree,
  Settings, BookOpen, Award,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import StatCard from './admin/StatCard';
import RecentActivityWidget from './admin/RecentActivityWidget';
import ExcelImportSection from './admin/ExcelImportSection';
import ExcelGuideDialog from './admin/ExcelGuideDialog';
import CategoriesSection from './admin/CategoriesSection';
import ProductsTable from './admin/ProductsSection';
import JobsTable from './admin/JobsSection';
import EmployeesTable from './admin/EmployeesSection';
import ApplicationsTable from './admin/ApplicationsTable';
import LibraryTable from './admin/LibrarySection';
import ArticlesTable from './admin/ArticlesSection';
import CertificationsSection from './admin/CertificationsSection';
import SiteSettingsSection from './admin/SiteSettingsSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const { language } = useLanguage();
  const { logout } = useAuth();
  const [stats, setStats] = useState({ products: 0, active_jobs: 0, applications: 0, employees: 0 });
  const [products, setProducts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [applications, setApplications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [libraryDocs, setLibraryDocs] = useState([]);
  const [articles, setArticles] = useState([]);
  const [excelFile, setExcelFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [excelGuide, setExcelGuide] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    fetchAllData();
    fetchExcelGuide();
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSiteSettings(res.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchAllData = async () => {
    try {
      const [statsRes, productsRes, jobsRes, employeesRes, applicationsRes, categoriesRes, libraryRes, articlesRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/products`),
        axios.get(`${API}/jobs?active_only=false`),
        axios.get(`${API}/employees`),
        axios.get(`${API}/applications`),
        axios.get(`${API}/categories/tree`),
        axios.get(`${API}/library`),
        axios.get(`${API}/articles?published_only=false&limit=200`),
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setJobs(jobsRes.data);
      setEmployees(employeesRes.data);
      setApplications(applicationsRes.data);
      setCategories(categoriesRes.data);
      setLibraryDocs(libraryRes.data);
      setArticles(articlesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchExcelGuide = async () => {
    try {
      const res = await axios.get(`${API}/products/excel-template/guide`);
      setExcelGuide(res.data);
    } catch (error) {
      console.error('Error fetching excel guide:', error);
    }
  };

  const handleExcelImport = async () => {
    if (!excelFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      const res = await axios.post(`${API}/products/import-excel`, formData);
      toast.success(res.data.message);
      setExcelFile(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(language === 'sv' ? 'Är du säker?' : 'Are you sure?')) return;
    try {
      await axios.delete(`${API}/${type}/${id}`);
      toast.success(language === 'sv' ? 'Borttaget' : 'Deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleToggleJobActive = async (id) => {
    try {
      await axios.patch(`${API}/jobs/${id}/toggle-active`);
      fetchAllData();
    } catch (error) {
      toast.error('Error');
    }
  };

  const handleUpdateApplicationStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/applications/${id}/status?status=${status}`);
      fetchAllData();
    } catch (error) {
      toast.error('Error');
    }
  };

  return (
    <div data-testid="admin-page" className="bg-[#FAFAF8] min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">
            {language === 'sv' ? 'Admin-panel' : 'Admin Panel'}
          </h1>
          <Button onClick={logout} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 rounded-none">
            <LogOut className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Logga ut' : 'Log out'}
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Package} value={stats.products} label={language === 'sv' ? 'Produkter' : 'Products'} />
          <StatCard icon={Briefcase} value={stats.active_jobs} label={language === 'sv' ? 'Aktiva jobb' : 'Active Jobs'} />
          <StatCard icon={FileText} value={stats.applications} label={language === 'sv' ? 'Ansökningar' : 'Applications'} />
          <StatCard icon={Users} value={stats.employees} label={language === 'sv' ? 'Medarbetare' : 'Employees'} />
        </div>

        <RecentActivityWidget limit={8} />

        <Tabs defaultValue="products" className="w-full">
          <TabsList className="bg-white border border-stone-200 p-1 mb-6 rounded-none flex-wrap">
            <TabsTrigger value="products" className="data-[state=active]:bg-stone-100 rounded-none">
              <Package className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Produkter' : 'Products'}
            </TabsTrigger>
            <TabsTrigger value="categories" className="data-[state=active]:bg-stone-100 rounded-none">
              <FolderTree className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Kategorier' : 'Categories'}
            </TabsTrigger>
            <TabsTrigger value="jobs" className="data-[state=active]:bg-stone-100 rounded-none">
              <Briefcase className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Jobb' : 'Jobs'}
            </TabsTrigger>
            <TabsTrigger value="employees" className="data-[state=active]:bg-stone-100 rounded-none">
              <Users className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Medarbetare' : 'Employees'}
            </TabsTrigger>
            <TabsTrigger value="library" className="data-[state=active]:bg-stone-100 rounded-none">
              <BookOpen className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Bibliotek' : 'Library'}
            </TabsTrigger>
            <TabsTrigger value="articles" className="data-[state=active]:bg-stone-100 rounded-none" data-testid="tab-articles">
              <BookOpen className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Kunskap' : 'Knowledge'}
            </TabsTrigger>
            <TabsTrigger value="applications" className="data-[state=active]:bg-stone-100 rounded-none">
              <FileText className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Ansökningar' : 'Applications'}
            </TabsTrigger>
            <TabsTrigger value="certifications" className="data-[state=active]:bg-stone-100 rounded-none" data-testid="tab-certifications">
              <Award className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Märkningar' : 'Certifications'}
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-stone-100 rounded-none">
              <Settings className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Inställningar' : 'Settings'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products">
            <ExcelImportSection excelFile={excelFile} setExcelFile={setExcelFile} handleExcelImport={handleExcelImport} importing={importing} setShowGuide={setShowGuide} language={language} />
            <ProductsTable products={products} categories={categories} language={language} onDelete={(id) => handleDelete('products', id)} onRefresh={fetchAllData} />
          </TabsContent>

          <TabsContent value="categories">
            <CategoriesSection categories={categories} language={language} onRefresh={fetchAllData} onDelete={(id) => handleDelete('categories', id)} />
          </TabsContent>

          <TabsContent value="jobs">
            <JobsTable jobs={jobs} language={language} onDelete={(id) => handleDelete('jobs', id)} onToggle={handleToggleJobActive} onRefresh={fetchAllData} />
          </TabsContent>

          <TabsContent value="employees">
            <EmployeesTable employees={employees} language={language} onDelete={(id) => handleDelete('employees', id)} onRefresh={fetchAllData} />
          </TabsContent>

          <TabsContent value="library">
            <LibraryTable libraryDocs={libraryDocs} language={language} onDelete={(id) => handleDelete('library', id)} onRefresh={fetchAllData} />
          </TabsContent>

          <TabsContent value="articles">
            <ArticlesTable articles={articles} products={products} language={language} onDelete={(id) => handleDelete('articles', id)} onRefresh={fetchAllData} />
          </TabsContent>

          <TabsContent value="applications">
            <ApplicationsTable applications={applications} language={language} onStatusChange={handleUpdateApplicationStatus} />
          </TabsContent>

          <TabsContent value="certifications">
            <CertificationsSection language={language} />
          </TabsContent>

          <TabsContent value="settings">
            <SiteSettingsSection settings={siteSettings} language={language} onRefresh={fetchSiteSettings} />
          </TabsContent>
        </Tabs>

        <ExcelGuideDialog open={showGuide} onOpenChange={setShowGuide} guide={excelGuide} language={language} />
      </div>
    </div>
  );
};

export default AdminPage;
